import { Languages, Moon, Settings2, Sun, SunMoon } from 'lucide-react';
import { LOCALE_NAMES, LOCALES, useT, type Locale } from '../../i18n';
import { THEME_PREFERENCES, type ThemePreference } from '../../theme/theme';
import { useTheme } from '../../theme/useTheme';
import { Menu, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator } from '../ui/menu';
import { cn } from '../../lib/cn';

const THEME_ICONS: Record<ThemePreference, typeof Sun> = {
  system: SunMoon,
  light: Sun,
  dark: Moon,
};

/**
 * Appearance and language in one place, reachable both from the application
 * chrome and from the login screen — the language has to be switchable before
 * anyone signs in.
 */
export const PreferenceMenu = ({ className }: { className?: string }) => {
  const { t, locale, setLocale } = useT();
  const { preference, resolved, setPreference } = useTheme();

  const themeLabel: Record<ThemePreference, string> = {
    system: t('nav.themeSystem'),
    light: t('nav.themeLight'),
    dark: t('nav.themeDark'),
  };

  const TriggerIcon = resolved === 'dark' ? Moon : Sun;

  return (
    <Menu
      trigger={
        <button
          type="button"
          title={`${t('nav.appearance')} · ${t('nav.language')}`}
          aria-label={`${t('nav.appearance')} · ${t('nav.language')}`}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-sunken hover:text-ink',
            className,
          )}
        >
          <Settings2 className="size-4" />
        </button>
      }
    >
      <MenuLabel>
        <span className="flex items-center gap-1.5">
          <TriggerIcon className="size-3" />
          {t('nav.appearance')}
        </span>
      </MenuLabel>
      <MenuRadioGroup
        value={preference}
        onValueChange={(value) => setPreference(value as ThemePreference)}
      >
        {THEME_PREFERENCES.map((option) => {
          const Icon = THEME_ICONS[option];
          return (
            <MenuRadioItem key={option} value={option}>
              <Icon className="size-3.5 text-ink-subtle" />
              {themeLabel[option]}
            </MenuRadioItem>
          );
        })}
      </MenuRadioGroup>

      <MenuSeparator />

      <MenuLabel>
        <span className="flex items-center gap-1.5">
          <Languages className="size-3" />
          {t('nav.language')}
        </span>
      </MenuLabel>
      <MenuRadioGroup value={locale} onValueChange={(value) => setLocale(value as Locale)}>
        {LOCALES.map((option) => (
          <MenuRadioItem key={option} value={option}>
            {LOCALE_NAMES[option]}
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </Menu>
  );
};
